// packages/kern/src/migrate.ts — fehlende Felder und Normalisierung (SPEC §3.12).
//
// KEIN DOM: außer `text-canvas.ts` darf keine Datei in `packages/kern/src`
// `document`, `window`, `navigator`, `fetch`, `localStorage` oder einen
// React-Import enthalten.
//
// `normalisiere` arbeitet AUF DEM ÜBERGEBENEN OBJEKT (wie im Prototyp) und
// gibt es zurück. Wer eine Kopie braucht, ruft `normalisiere(klon(plan))`.
//
// Vorlage: `docs/quellen/understand/prototype.json`, reusable_snippets[20]
// (`normalize()`), mit der Änderung aus B8: Einträge mit unbekannter Spur
// werden UMGEHÄNGT und gezählt, nicht verworfen.
//
// `migriere` ist der Eingang für FREMDE Daten — eine hochgeladene JSON-Datei,
// das Ergebnis eines PDF-Imports, ein Entwurf aus dem localStorage. Darum
// steht Schritt 4 der Spezifikation („keine endliche Zahl -> Vorgabe") hier
// konsequent an JEDEM Zahlenfeld, auch an `a`, `b`, `size`, `row` und an
// `start`/`end`: ein einziges NaN im Zeitraum macht `minMi`, `snapMi` und
// `clampMi` zu NaN und den Editor unbedienbar.

import { ICON_ORDER } from "./icons";
import { aufHalbe, HALBZEILE, RAND_MAX_MM, RAND_MIN_MM, RAND_VORGABE_MM } from "./layout";
import { PRESETS } from "./presets";
import { STYLES } from "./styles";
import { TB } from "./texte";
import type {
  Ausrichtung, BlattFormat, Eintrag, EigenerPfad, EigenesPiktogramm, PiktogrammKey, PiktogrammLage, Plan,
  Spur, StilKey,
} from "./types";
import { maxMi, minMi, parseYM, uid } from "./zeit";

const STIL_MENGE: ReadonlySet<string> = new Set<string>(Object.keys(STYLES));
const PIKTO_MENGE: ReadonlySet<string> = new Set<string>(ICON_ORDER);
const LAGE_MENGE: ReadonlySet<string> = new Set<string>(["left", "center", "right"]);
const FORMAT_MENGE: ReadonlySet<string> = new Set<string>(["frei", "a4q", "a3q"]);

const istStil = (x: unknown): x is StilKey => typeof x === "string" && STIL_MENGE.has(x);
const istPikto = (x: unknown): x is PiktogrammKey => typeof x === "string" && PIKTO_MENGE.has(x);
const istLage = (x: unknown): x is PiktogrammLage => typeof x === "string" && LAGE_MENGE.has(x);
const istAusrichtung = (x: unknown): x is Ausrichtung => typeof x === "string" && LAGE_MENGE.has(x);
const istFormat = (x: unknown): x is BlattFormat => typeof x === "string" && FORMAT_MENGE.has(x);

function istObjekt(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

/** Zahl in die Grenzen. */
const klemme = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/** `v`, wenn es eine endliche Zahl ist, sonst die Vorgabe. */
const zahl = (v: unknown, vorgabe: number): number =>
  typeof v === "number" && Number.isFinite(v) ? v : vorgabe;

/** `v`, wenn es ein als "YYYY-MM" lesbarer Monat ist, sonst die Vorgabe. */
const monat = (v: unknown, vorgabe: string): string =>
  typeof v === "string" && Number.isFinite(parseYM(v)) ? v : vorgabe;

// Eigene Symbole (T44). Dieselben Muster wie im Server (`piktogramm_import.py`):
// was hier nicht passt, war nie ein vom Server gelesenes Symbol, sondern von Hand
// oder von fremder Hand in die Datei geschrieben.
const D_OK = /^[MmZzLlHhVvCcSsQqTtAa0-9eE.,+\-\s]+$/;
const UMFORMUNG_OK = /^\s*(?:(?:matrix|translate|scale|rotate|skewX|skewY)\s*\(\s*[-+0-9eE.,\s]+\)\s*,?\s*)+$/;

/** Ein eingebettetes eigenes Symbol prüfen. `undefined` = nicht brauchbar. */
function eigenesPruefen(roh: unknown): EigenesPiktogramm | undefined {
  if (!istObjekt(roh)) return undefined;
  const vb = roh["viewBox"];
  if (!Array.isArray(vb) || vb.length !== 4) return undefined;
  const zahlen = vb.map((n) => (typeof n === "number" && Number.isFinite(n) ? n : NaN));
  if (zahlen.some((n) => Number.isNaN(n)) || !(zahlen[2]! > 0 && zahlen[3]! > 0)) return undefined;
  const liste = roh["pfade"];
  if (!Array.isArray(liste) || liste.length > 300) return undefined;
  const pfade: EigenerPfad[] = [];
  for (const p of liste as unknown[]) {
    if (!istObjekt(p)) continue;
    const art = p["art"], d = p["d"], t = p["transform"];
    if ((art !== "f" && art !== "p" && art !== "s") || typeof d !== "string") continue;
    if (d.length > 20000 || !D_OK.test(d)) continue;
    // Eine Umformung, die nicht lesbar ist, lässt den Pfad weg — falsch gezeichnet
    // wäre schlimmer als fehlend.
    if (t !== undefined && (typeof t !== "string" || t.length > 300 || !UMFORMUNG_OK.test(t))) continue;
    const e: EigenerPfad = { art, d };
    if (art === "s") e.sw = klemme(zahl(p["sw"], 1), 0.05, 50);
    if (p["fillRule"] === "evenodd") e.fillRule = "evenodd";
    if (typeof t === "string") e.transform = t;
    if (p["cap"] === "round" || p["cap"] === "square") e.cap = p["cap"];
    if (p["join"] === "round" || p["join"] === "bevel") e.join = p["join"];
    pfade.push(e);
  }
  if (!pfade.length) return undefined;
  const id = roh["id"], name = roh["name"];
  return {
    id: typeof id === "string" ? id.slice(0, 64) : "",
    name: typeof name === "string" ? name.slice(0, 60) : "",
    viewBox: [zahlen[0]!, zahlen[1]!, zahlen[2]!, zahlen[3]!],
    pfade,
  };
}

/**
 * Ergaenzt fehlende Felder aus PRESETS.leer und klemmt Werte. `v` bleibt 1.
 *
 * Wirft `Error("Format")`, wenn das Rohe kein Plan sein kann — der Aufrufer
 * zeigt dann „Datei konnte nicht gelesen werden".
 */
export function migriere(rohes: unknown): Plan {
  if (!istObjekt(rohes)) throw new Error("Format");
  const roheSpuren = rohes["lanes"], roheEintraege = rohes["items"];
  if (!Array.isArray(roheSpuren) || !Array.isArray(roheEintraege)) throw new Error("Format");
  const spurenListe: unknown[] = roheSpuren;
  const eintragListe: unknown[] = roheEintraege;

  // Dieselbe Richtung wie im Prototyp: die Vorlage liefert die Vorgaben, die
  // Datei gewinnt. Unbekannte Schlüssel (A.5: kunde, projektNr, schlagworte,
  // vorlageName) bleiben unangetastet stehen — der Zeichner ignoriert sie.
  const vorgabe = PRESETS.leer.build();
  const p: Plan = Object.assign(PRESETS.leer.build(), rohes);
  p.v = 1;

  p.title = typeof p.title === "string" ? p.title : vorgabe.title;
  p.start = monat(p.start, vorgabe.start);
  p.end = monat(p.end, vorgabe.end);
  p.compressFrom = typeof p.compressFrom === "string" && Number.isFinite(parseYM(p.compressFrom))
    ? p.compressFrom : "";
  p.bl = typeof p.bl === "string" ? p.bl : "";
  // Plaene aus der Zeit vor dem Blattmodell (T25a) haben kein `format`. Sie
  // bekommen "frei" und verhalten sich damit Pixel fuer Pixel wie zuvor —
  // das ist der Grund, warum die Golden-Dateien unveraendert gelten.
  p.format = istFormat(p.format) ? p.format : "frei";
  p.fitRows = typeof p.fitRows === "boolean" ? p.fitRows : true;
  p.ferien = !!p.ferien;
  p.ferienNames = !!p.ferienNames;
  p.guides = !!p.guides;
  // Fehlt bei allen Plänen von vor T41 — und „nicht drucken" ist genau das
  // bisherige Verhalten.
  p.guidesExport = !!p.guidesExport;

  // Zahlenfelder: keine endliche Zahl -> Vorgabe, danach klemmen (§3.12 4/5).
  p.compressW = klemme(zahl(p.compressW, 1.25), 0.8, 3);
  p.rowH = klemme(zahl(p.rowH, 44), 28, 72);
  p.width = klemme(zahl(p.width, 1280), 900, 2600);
  // Dieselben Zahlen wie in `layout()` — und zwar buchstaeblich dieselben,
  // damit ein Plan aus `migriere()` und ein Bestandsplan ohne das Feld
  // nicht verschieden gerechnet werden.
  p.margin = klemme(zahl(p.margin, RAND_VORGABE_MM), RAND_MIN_MM, RAND_MAX_MM);
  p.fontScale = klemme(zahl(p.fontScale, 1), 0.75, 1.4);
  p.ferienMin = klemme(zahl(p.ferienMin, 3), 1, 10);
  p.grid = zahl(p.grid, 0.5);

  const spuren: Spur[] = [];
  for (const rohe of spurenListe) {
    if (!istObjekt(rohe)) continue;
    const id = rohe["id"], name = rohe["name"], def = rohe["def"];
    spuren.push({
      id: typeof id === "string" && id ? id : uid(),
      name: typeof name === "string" ? name : TB.spurFallback,
      rows: Math.max(1, Math.min(24, Number(rohe["rows"]) | 0)),
      def: istStil(def) ? def : "solid",
    });
  }
  if (!spuren.length) spuren.push({ id: uid(), name: TB.spurFallback, rows: 2, def: "solid" });
  p.lanes = spuren;

  const anfang = parseYM(p.start);
  const eintraege: Eintrag[] = [];
  for (const rohe of eintragListe) {
    if (!istObjekt(rohe)) continue;
    const lane = typeof rohe["lane"] === "string" ? rohe["lane"] : "";
    const spur = spuren.find((s) => s.id === lane);
    const id = rohe["id"], text = rohe["text"], color = rohe["color"];
    const style = rohe["style"], icon = rohe["icon"];
    const iconPos = rohe["iconPos"], align = rohe["align"];
    const a = zahl(rohe["a"], anfang);
    const eigen = icon === "eigen" ? eigenesPruefen(rohe["eigenesIcon"]) : undefined;
    eintraege.push({
      id: typeof id === "string" && id ? id : uid(),
      lane,
      // `Number()` statt `zahl()`: "2" aus einer Tabelle bleibt 2, wie vorher.
      // Aber auf HALBE Zeilen, nicht `| 0` — sonst fiele 2,5 still auf 2 (T42).
      row: aufHalbe(Math.max(0, Number.isFinite(Number(rohe["row"])) ? Number(rohe["row"]) : 0)),
      a,
      b: zahl(rohe["b"], a + 0.5),
      text: typeof text === "string" ? text.slice(0, 4000) : "",
      style: istStil(style) ? style : (spur ? spur.def : "solid"),
      icon: eigen ? "eigen" : istPikto(icon) ? icon : "none",
      iconPos: istLage(iconPos) ? iconPos : "center",
      align: istAusrichtung(align) ? align : "center",
      color: typeof color === "string" && /^#[0-9a-f]{6}$/i.test(color) ? color : "",
      size: zahl(rohe["size"], 0),
      hoehe: Math.max(HALBZEILE, aufHalbe(zahl(rohe["hoehe"], 1))),
      drehung: rohe["drehung"] === 90 ? 90 : 0,
      iconColor: typeof rohe["iconColor"] === "string" && /^#[0-9a-f]{6}$/i.test(rohe["iconColor"] as string)
        ? (rohe["iconColor"] as string) : "",
      frei: rohe["frei"] === true,
      // Versatz auf ± 5000 px begrenzt: ein Tippfehler im JSON soll den
      // Eintrag nicht in einen anderen Landkreis schieben.
      dx: klemme(zahl(rohe["dx"], 0), -5000, 5000),
      dy: klemme(zahl(rohe["dy"], 0), -5000, 5000),
      // Nur wenn es wirklich ein brauchbares eigenes Symbol ist (T44).
      ...(eigen ? { eigenesIcon: eigen } : {}),
    });
  }
  p.items = eintraege;

  return normalisiere(p);
}

/**
 * Aufraeumen nach Aenderungen am Zeitraum, an Spuren oder nach einem Import.
 * Arbeitet auf dem uebergebenen Objekt und gibt es zurueck.
 */
export function normalisiere(plan: Plan): Plan {
  return normalisiereMitBericht(plan).plan;
}

/** Wie `normalisiere()`, nennt aber die Zahl der umgehaengten Eintraege. */
export function normalisiereMitBericht(plan: Plan): { plan: Plan; umgehaengt: number } {
  const lo = minMi(plan), hi = maxMi(plan);

  plan.items.forEach((it) => {
    if (!(it.b > it.a)) it.b = it.a + 0.5;
  });

  if (plan.compressFrom) {
    const c = parseYM(plan.compressFrom);
    if (!Number.isFinite(c) || c < lo || c >= hi) plan.compressFrom = "";
  }

  // B8: Einträge mit unbekannter Spur werden auf die erste Spur gesetzt und
  // GEZÄHLT — der stille Datenverlust beim Import war ein Mangel des
  // Prototyps, und die Import-Prüfansicht muss die Zahl nennen können.
  // Ohne jede Spur gibt es nichts zum Umhängen; die Einträge bleiben dann
  // liegen (`buildScene` zeichnet sie nicht und legt keinen Treffer an).
  let umgehaengt = 0;
  const erste = plan.lanes[0];
  if (erste) {
    const ids = new Set(plan.lanes.map((l) => l.id));
    plan.items.forEach((it) => {
      if (!ids.has(it.lane)) { it.lane = erste.id; it.row = 0; umgehaengt++; }
    });
  }

  // Halbe Zeilen (T42): Höhe und Beginn auf 0,5 runden, der Kasten bleibt
  // ganz in seiner Spur. Bei `hoehe` 1 ist das genau die alte Klemme
  // `0 … rows − 1`.
  plan.lanes.forEach((l) => {
    plan.items.forEach((it) => {
      if (it.lane !== l.id) return;
      const hoch = Number.isFinite(it.hoehe) ? it.hoehe : 1;
      it.hoehe = Math.max(HALBZEILE, Math.min(l.rows, aufHalbe(hoch)));
      const r = Number.isFinite(it.row) ? it.row : 0;
      it.row = Math.max(0, Math.min(l.rows - it.hoehe, aufHalbe(r)));
    });
  });

  return { plan, umgehaengt };
}
